import { DmaCountry } from './dma-countries';

/**
 * Every ISO 3166-1 country, plus Kosovo's user-assigned `XK` / `XKX`, keyed by
 * alpha-2 and pointing at the `DMA_COUNTRIES` name operational data uses.
 *
 * The codes and their coverage follow company-mapping's `Screening/countries.py`,
 * which normalises the countries the site-identification service writes. Where
 * that table names a country differently (Türkiye, São Tomé and Príncipe) the
 * value here is the DMA spelling, since that is what the trigger sheet matches on.
 */
export const dmaCountryByAlpha2: Record<string, { alpha3: string; dmaCountry: DmaCountry }> = {
  AD: { alpha3: 'AND', dmaCountry: 'Andorra' },
  AE: { alpha3: 'ARE', dmaCountry: 'United Arab Emirates' },
  AF: { alpha3: 'AFG', dmaCountry: 'Afghanistan' },
  AG: { alpha3: 'ATG', dmaCountry: 'Antigua and Barbuda' },
  AI: { alpha3: 'AIA', dmaCountry: 'Anguilla' },
  AL: { alpha3: 'ALB', dmaCountry: 'Albania' },
  AM: { alpha3: 'ARM', dmaCountry: 'Armenia' },
  AO: { alpha3: 'AGO', dmaCountry: 'Angola' },
  AQ: { alpha3: 'ATA', dmaCountry: 'Antarctica' },
  AR: { alpha3: 'ARG', dmaCountry: 'Argentina' },
  AS: { alpha3: 'ASM', dmaCountry: 'American Samoa' },
  AT: { alpha3: 'AUT', dmaCountry: 'Austria' },
  AU: { alpha3: 'AUS', dmaCountry: 'Australia' },
  AW: { alpha3: 'ABW', dmaCountry: 'Aruba' },
  AX: { alpha3: 'ALA', dmaCountry: 'Åland Islands' },
  AZ: { alpha3: 'AZE', dmaCountry: 'Azerbaijan' },
  BA: { alpha3: 'BIH', dmaCountry: 'Bosnia and Herzegovina' },
  BB: { alpha3: 'BRB', dmaCountry: 'Barbados' },
  BD: { alpha3: 'BGD', dmaCountry: 'Bangladesh' },
  BE: { alpha3: 'BEL', dmaCountry: 'Belgium' },
  BF: { alpha3: 'BFA', dmaCountry: 'Burkina Faso' },
  BG: { alpha3: 'BGR', dmaCountry: 'Bulgaria' },
  BH: { alpha3: 'BHR', dmaCountry: 'Bahrain' },
  BI: { alpha3: 'BDI', dmaCountry: 'Burundi' },
  BJ: { alpha3: 'BEN', dmaCountry: 'Benin' },
  BL: { alpha3: 'BLM', dmaCountry: 'Saint Barthélemy' },
  BM: { alpha3: 'BMU', dmaCountry: 'Bermuda' },
  BN: { alpha3: 'BRN', dmaCountry: 'Brunei' },
  BO: { alpha3: 'BOL', dmaCountry: 'Bolivia' },
  BQ: { alpha3: 'BES', dmaCountry: 'Bonaire, Sint Eustatius and Saba' },
  BR: { alpha3: 'BRA', dmaCountry: 'Brazil' },
  BS: { alpha3: 'BHS', dmaCountry: 'Bahamas' },
  BT: { alpha3: 'BTN', dmaCountry: 'Bhutan' },
  BV: { alpha3: 'BVT', dmaCountry: 'Bouvet Island' },
  BW: { alpha3: 'BWA', dmaCountry: 'Botswana' },
  BY: { alpha3: 'BLR', dmaCountry: 'Belarus' },
  BZ: { alpha3: 'BLZ', dmaCountry: 'Belize' },
  CA: { alpha3: 'CAN', dmaCountry: 'Canada' },
  CC: { alpha3: 'CCK', dmaCountry: 'Cocos (Keeling) Islands' },
  CD: { alpha3: 'COD', dmaCountry: 'Democratic Republic of the Congo' },
  CF: { alpha3: 'CAF', dmaCountry: 'Central African Republic' },
  CG: { alpha3: 'COG', dmaCountry: 'Republic of the Congo' },
  CH: { alpha3: 'CHE', dmaCountry: 'Switzerland' },
  CI: { alpha3: 'CIV', dmaCountry: "Côte d'Ivoire" },
  CK: { alpha3: 'COK', dmaCountry: 'Cook Islands' },
  CL: { alpha3: 'CHL', dmaCountry: 'Chile' },
  CM: { alpha3: 'CMR', dmaCountry: 'Cameroon' },
  CN: { alpha3: 'CHN', dmaCountry: 'China' },
  CO: { alpha3: 'COL', dmaCountry: 'Colombia' },
  CR: { alpha3: 'CRI', dmaCountry: 'Costa Rica' },
  CU: { alpha3: 'CUB', dmaCountry: 'Cuba' },
  CV: { alpha3: 'CPV', dmaCountry: 'Cabo Verde' },
  CW: { alpha3: 'CUW', dmaCountry: 'Curaçao' },
  CX: { alpha3: 'CXR', dmaCountry: 'Christmas Island' },
  CY: { alpha3: 'CYP', dmaCountry: 'Cyprus' },
  CZ: { alpha3: 'CZE', dmaCountry: 'Czechia' },
  DE: { alpha3: 'DEU', dmaCountry: 'Germany' },
  DJ: { alpha3: 'DJI', dmaCountry: 'Djibouti' },
  DK: { alpha3: 'DNK', dmaCountry: 'Denmark' },
  DM: { alpha3: 'DMA', dmaCountry: 'Dominica' },
  DO: { alpha3: 'DOM', dmaCountry: 'Dominican Republic' },
  DZ: { alpha3: 'DZA', dmaCountry: 'Algeria' },
  EC: { alpha3: 'ECU', dmaCountry: 'Ecuador' },
  EE: { alpha3: 'EST', dmaCountry: 'Estonia' },
  EG: { alpha3: 'EGY', dmaCountry: 'Egypt' },
  EH: { alpha3: 'ESH', dmaCountry: 'Western Sahara' },
  ER: { alpha3: 'ERI', dmaCountry: 'Eritrea' },
  ES: { alpha3: 'ESP', dmaCountry: 'Spain' },
  ET: { alpha3: 'ETH', dmaCountry: 'Ethiopia' },
  FI: { alpha3: 'FIN', dmaCountry: 'Finland' },
  FJ: { alpha3: 'FJI', dmaCountry: 'Fiji' },
  FK: { alpha3: 'FLK', dmaCountry: 'Falkland Islands' },
  FM: { alpha3: 'FSM', dmaCountry: 'Micronesia' },
  FO: { alpha3: 'FRO', dmaCountry: 'Faroe Islands' },
  FR: { alpha3: 'FRA', dmaCountry: 'France' },
  GA: { alpha3: 'GAB', dmaCountry: 'Gabon' },
  GB: { alpha3: 'GBR', dmaCountry: 'United Kingdom' },
  GD: { alpha3: 'GRD', dmaCountry: 'Grenada' },
  GE: { alpha3: 'GEO', dmaCountry: 'Georgia' },
  GF: { alpha3: 'GUF', dmaCountry: 'French Guiana' },
  GG: { alpha3: 'GGY', dmaCountry: 'Guernsey' },
  GH: { alpha3: 'GHA', dmaCountry: 'Ghana' },
  GI: { alpha3: 'GIB', dmaCountry: 'Gibraltar' },
  GL: { alpha3: 'GRL', dmaCountry: 'Greenland' },
  GM: { alpha3: 'GMB', dmaCountry: 'Gambia' },
  GN: { alpha3: 'GIN', dmaCountry: 'Guinea' },
  GP: { alpha3: 'GLP', dmaCountry: 'Guadeloupe' },
  GQ: { alpha3: 'GNQ', dmaCountry: 'Equatorial Guinea' },
  GR: { alpha3: 'GRC', dmaCountry: 'Greece' },
  GS: { alpha3: 'SGS', dmaCountry: 'South Georgia and the South Sandwich Islands' },
  GT: { alpha3: 'GTM', dmaCountry: 'Guatemala' },
  GU: { alpha3: 'GUM', dmaCountry: 'Guam' },
  GW: { alpha3: 'GNB', dmaCountry: 'Guinea-Bissau' },
  GY: { alpha3: 'GUY', dmaCountry: 'Guyana' },
  HK: { alpha3: 'HKG', dmaCountry: 'Hong Kong' },
  HM: { alpha3: 'HMD', dmaCountry: 'Heard Island and McDonald Islands' },
  HN: { alpha3: 'HND', dmaCountry: 'Honduras' },
  HR: { alpha3: 'HRV', dmaCountry: 'Croatia' },
  HT: { alpha3: 'HTI', dmaCountry: 'Haiti' },
  HU: { alpha3: 'HUN', dmaCountry: 'Hungary' },
  ID: { alpha3: 'IDN', dmaCountry: 'Indonesia' },
  IE: { alpha3: 'IRL', dmaCountry: 'Ireland' },
  IL: { alpha3: 'ISR', dmaCountry: 'Israel' },
  IM: { alpha3: 'IMN', dmaCountry: 'Isle of Man' },
  IN: { alpha3: 'IND', dmaCountry: 'India' },
  IO: { alpha3: 'IOT', dmaCountry: 'British Indian Ocean Territory' },
  IQ: { alpha3: 'IRQ', dmaCountry: 'Iraq' },
  IR: { alpha3: 'IRN', dmaCountry: 'Iran' },
  IS: { alpha3: 'ISL', dmaCountry: 'Iceland' },
  IT: { alpha3: 'ITA', dmaCountry: 'Italy' },
  JE: { alpha3: 'JEY', dmaCountry: 'Jersey' },
  JM: { alpha3: 'JAM', dmaCountry: 'Jamaica' },
  JO: { alpha3: 'JOR', dmaCountry: 'Jordan' },
  JP: { alpha3: 'JPN', dmaCountry: 'Japan' },
  KE: { alpha3: 'KEN', dmaCountry: 'Kenya' },
  KG: { alpha3: 'KGZ', dmaCountry: 'Kyrgyzstan' },
  KH: { alpha3: 'KHM', dmaCountry: 'Cambodia' },
  KI: { alpha3: 'KIR', dmaCountry: 'Kiribati' },
  KM: { alpha3: 'COM', dmaCountry: 'Comoros' },
  KN: { alpha3: 'KNA', dmaCountry: 'Saint Kitts and Nevis' },
  KP: { alpha3: 'PRK', dmaCountry: 'North Korea' },
  KR: { alpha3: 'KOR', dmaCountry: 'South Korea' },
  KW: { alpha3: 'KWT', dmaCountry: 'Kuwait' },
  KY: { alpha3: 'CYM', dmaCountry: 'Cayman Islands' },
  KZ: { alpha3: 'KAZ', dmaCountry: 'Kazakhstan' },
  LA: { alpha3: 'LAO', dmaCountry: 'Laos' },
  LB: { alpha3: 'LBN', dmaCountry: 'Lebanon' },
  LC: { alpha3: 'LCA', dmaCountry: 'Saint Lucia' },
  LI: { alpha3: 'LIE', dmaCountry: 'Liechtenstein' },
  LK: { alpha3: 'LKA', dmaCountry: 'Sri Lanka' },
  LR: { alpha3: 'LBR', dmaCountry: 'Liberia' },
  LS: { alpha3: 'LSO', dmaCountry: 'Lesotho' },
  LT: { alpha3: 'LTU', dmaCountry: 'Lithuania' },
  LU: { alpha3: 'LUX', dmaCountry: 'Luxembourg' },
  LV: { alpha3: 'LVA', dmaCountry: 'Latvia' },
  LY: { alpha3: 'LBY', dmaCountry: 'Libya' },
  MA: { alpha3: 'MAR', dmaCountry: 'Morocco' },
  MC: { alpha3: 'MCO', dmaCountry: 'Monaco' },
  MD: { alpha3: 'MDA', dmaCountry: 'Moldova' },
  ME: { alpha3: 'MNE', dmaCountry: 'Montenegro' },
  MF: { alpha3: 'MAF', dmaCountry: 'Saint Martin (French part)' },
  MG: { alpha3: 'MDG', dmaCountry: 'Madagascar' },
  MH: { alpha3: 'MHL', dmaCountry: 'Marshall Islands' },
  MK: { alpha3: 'MKD', dmaCountry: 'North Macedonia' },
  ML: { alpha3: 'MLI', dmaCountry: 'Mali' },
  MM: { alpha3: 'MMR', dmaCountry: 'Myanmar' },
  MN: { alpha3: 'MNG', dmaCountry: 'Mongolia' },
  MO: { alpha3: 'MAC', dmaCountry: 'Macao' },
  MP: { alpha3: 'MNP', dmaCountry: 'Northern Mariana Islands' },
  MQ: { alpha3: 'MTQ', dmaCountry: 'Martinique' },
  MR: { alpha3: 'MRT', dmaCountry: 'Mauritania' },
  MS: { alpha3: 'MSR', dmaCountry: 'Montserrat' },
  MT: { alpha3: 'MLT', dmaCountry: 'Malta' },
  MU: { alpha3: 'MUS', dmaCountry: 'Mauritius' },
  MV: { alpha3: 'MDV', dmaCountry: 'Maldives' },
  MW: { alpha3: 'MWI', dmaCountry: 'Malawi' },
  MX: { alpha3: 'MEX', dmaCountry: 'Mexico' },
  MY: { alpha3: 'MYS', dmaCountry: 'Malaysia' },
  MZ: { alpha3: 'MOZ', dmaCountry: 'Mozambique' },
  NA: { alpha3: 'NAM', dmaCountry: 'Namibia' },
  NC: { alpha3: 'NCL', dmaCountry: 'New Caledonia' },
  NE: { alpha3: 'NER', dmaCountry: 'Niger' },
  NF: { alpha3: 'NFK', dmaCountry: 'Norfolk Island' },
  NG: { alpha3: 'NGA', dmaCountry: 'Nigeria' },
  NI: { alpha3: 'NIC', dmaCountry: 'Nicaragua' },
  NL: { alpha3: 'NLD', dmaCountry: 'Netherlands' },
  NO: { alpha3: 'NOR', dmaCountry: 'Norway' },
  NP: { alpha3: 'NPL', dmaCountry: 'Nepal' },
  NR: { alpha3: 'NRU', dmaCountry: 'Nauru' },
  NU: { alpha3: 'NIU', dmaCountry: 'Niue' },
  NZ: { alpha3: 'NZL', dmaCountry: 'New Zealand' },
  OM: { alpha3: 'OMN', dmaCountry: 'Oman' },
  PA: { alpha3: 'PAN', dmaCountry: 'Panama' },
  PE: { alpha3: 'PER', dmaCountry: 'Peru' },
  PF: { alpha3: 'PYF', dmaCountry: 'French Polynesia' },
  PG: { alpha3: 'PNG', dmaCountry: 'Papua New Guinea' },
  PH: { alpha3: 'PHL', dmaCountry: 'Philippines' },
  PK: { alpha3: 'PAK', dmaCountry: 'Pakistan' },
  PL: { alpha3: 'POL', dmaCountry: 'Poland' },
  PM: { alpha3: 'SPM', dmaCountry: 'Saint Pierre and Miquelon' },
  PN: { alpha3: 'PCN', dmaCountry: 'Pitcairn' },
  PR: { alpha3: 'PRI', dmaCountry: 'Puerto Rico' },
  PS: { alpha3: 'PSE', dmaCountry: 'Palestine' },
  PT: { alpha3: 'PRT', dmaCountry: 'Portugal' },
  PW: { alpha3: 'PLW', dmaCountry: 'Palau' },
  PY: { alpha3: 'PRY', dmaCountry: 'Paraguay' },
  QA: { alpha3: 'QAT', dmaCountry: 'Qatar' },
  RE: { alpha3: 'REU', dmaCountry: 'Réunion' },
  RO: { alpha3: 'ROU', dmaCountry: 'Romania' },
  RS: { alpha3: 'SRB', dmaCountry: 'Serbia' },
  RU: { alpha3: 'RUS', dmaCountry: 'Russia' },
  RW: { alpha3: 'RWA', dmaCountry: 'Rwanda' },
  SA: { alpha3: 'SAU', dmaCountry: 'Saudi Arabia' },
  SB: { alpha3: 'SLB', dmaCountry: 'Solomon Islands' },
  SC: { alpha3: 'SYC', dmaCountry: 'Seychelles' },
  SD: { alpha3: 'SDN', dmaCountry: 'Sudan' },
  SE: { alpha3: 'SWE', dmaCountry: 'Sweden' },
  SG: { alpha3: 'SGP', dmaCountry: 'Singapore' },
  SH: { alpha3: 'SHN', dmaCountry: 'Saint Helena' },
  SI: { alpha3: 'SVN', dmaCountry: 'Slovenia' },
  SJ: { alpha3: 'SJM', dmaCountry: 'Svalbard and Jan Mayen' },
  SK: { alpha3: 'SVK', dmaCountry: 'Slovakia' },
  SL: { alpha3: 'SLE', dmaCountry: 'Sierra Leone' },
  SM: { alpha3: 'SMR', dmaCountry: 'San Marino' },
  SN: { alpha3: 'SEN', dmaCountry: 'Senegal' },
  SO: { alpha3: 'SOM', dmaCountry: 'Somalia' },
  SR: { alpha3: 'SUR', dmaCountry: 'Suriname' },
  SS: { alpha3: 'SSD', dmaCountry: 'South Sudan' },
  ST: { alpha3: 'STP', dmaCountry: 'Sao Tome and Principe' },
  SV: { alpha3: 'SLV', dmaCountry: 'El Salvador' },
  SX: { alpha3: 'SXM', dmaCountry: 'Sint Maarten (Dutch part)' },
  SY: { alpha3: 'SYR', dmaCountry: 'Syria' },
  SZ: { alpha3: 'SWZ', dmaCountry: 'Eswatini' },
  TC: { alpha3: 'TCA', dmaCountry: 'Turks and Caicos Islands' },
  TD: { alpha3: 'TCD', dmaCountry: 'Chad' },
  TF: { alpha3: 'ATF', dmaCountry: 'French Southern Territories' },
  TG: { alpha3: 'TGO', dmaCountry: 'Togo' },
  TH: { alpha3: 'THA', dmaCountry: 'Thailand' },
  TJ: { alpha3: 'TJK', dmaCountry: 'Tajikistan' },
  TK: { alpha3: 'TKL', dmaCountry: 'Tokelau' },
  TL: { alpha3: 'TLS', dmaCountry: 'Timor-Leste' },
  TM: { alpha3: 'TKM', dmaCountry: 'Turkmenistan' },
  TN: { alpha3: 'TUN', dmaCountry: 'Tunisia' },
  TO: { alpha3: 'TON', dmaCountry: 'Tonga' },
  TR: { alpha3: 'TUR', dmaCountry: 'Turkey' },
  TT: { alpha3: 'TTO', dmaCountry: 'Trinidad and Tobago' },
  TV: { alpha3: 'TUV', dmaCountry: 'Tuvalu' },
  TW: { alpha3: 'TWN', dmaCountry: 'Taiwan' },
  TZ: { alpha3: 'TZA', dmaCountry: 'Tanzania' },
  UA: { alpha3: 'UKR', dmaCountry: 'Ukraine' },
  UG: { alpha3: 'UGA', dmaCountry: 'Uganda' },
  UM: { alpha3: 'UMI', dmaCountry: 'United States Minor Outlying Islands' },
  US: { alpha3: 'USA', dmaCountry: 'United States' },
  UY: { alpha3: 'URY', dmaCountry: 'Uruguay' },
  UZ: { alpha3: 'UZB', dmaCountry: 'Uzbekistan' },
  VA: { alpha3: 'VAT', dmaCountry: 'Holy See' },
  VC: { alpha3: 'VCT', dmaCountry: 'Saint Vincent and the Grenadines' },
  VE: { alpha3: 'VEN', dmaCountry: 'Venezuela' },
  VG: { alpha3: 'VGB', dmaCountry: 'British Virgin Islands' },
  VI: { alpha3: 'VIR', dmaCountry: 'United States Virgin Islands' },
  VN: { alpha3: 'VNM', dmaCountry: 'Vietnam' },
  VU: { alpha3: 'VUT', dmaCountry: 'Vanuatu' },
  WF: { alpha3: 'WLF', dmaCountry: 'Wallis and Futuna' },
  WS: { alpha3: 'WSM', dmaCountry: 'Samoa' },
  XK: { alpha3: 'XKX', dmaCountry: 'Kosovo' },
  YE: { alpha3: 'YEM', dmaCountry: 'Yemen' },
  YT: { alpha3: 'MYT', dmaCountry: 'Mayotte' },
  ZA: { alpha3: 'ZAF', dmaCountry: 'South Africa' },
  ZM: { alpha3: 'ZMB', dmaCountry: 'Zambia' },
  ZW: { alpha3: 'ZWE', dmaCountry: 'Zimbabwe' },
};
